import { useEffect, useRef, useState } from 'react';
import type { ActiveSessionState } from '../services/api/coachActiveSession';
import type { SessionNudge } from './useSessionNudges';
import { computeSectionElapsedSeconds } from '../utils/sectionTiming';

/**
 * MED-1223 → MED-1242 — Agenda-Aware Pacing (two-stage overrun thresholds).
 *
 * Deterministic, client-side per-section pacing for the coach LIVE active
 * session page. On each tick it checks the coach's CURRENT authored section
 * against its `duration_minutes` and emits pacing nudges when they're behind.
 * This is clock math over state the page already has (current section +
 * per-section elapsed + authored budget) — NO session-intelligence involvement
 * (that's why it can pace per-section, which SI's closing-window PacingMonitor
 * can't).
 *
 * MED-1242 replaces MED-1223's single fixed grace (~2.5 min) — which fired far
 * too late on short sections (a 5-min section didn't prompt until ~60% over) —
 * with a two-stage model driven by the section's authored length:
 *   - Prompt 1 (early nudge): fires at planned + 1 min, regardless of length.
 *   - Prompt 2 (escalation):  fires at planned × 1.2 (~20% over), but ONLY on
 *                             sections longer than 5 min (on ≤ 5 min the 1.2×
 *                             threshold lands at or before +1 min, so a second
 *                             prompt would be out of order or back-to-back).
 * Plus a bounded re-entry re-prompt: returning to a section already past planned
 * re-nudges once after ~1 continuous minute back on it.
 *
 * The nudges are EXCEPTION signals ("you're behind"), not end-of-section
 * markers: an on-pace section advanced at/under budget never prompts, so a
 * perfectly-paced session produces zero pacing prompts (AC5).
 *
 * Behaviour, keyed to the MED-1242 acceptance criteria:
 *   AC1  — runs only while the in-session AI surface is live (which already
 *          requires the `live_session_delivery` flag); caller composes `enabled`.
 *   AC2  — prompt 1 at planned + 1 min, naming the section, length-independent.
 *   AC3  — prompt 2 at planned × 1.2 (~20% over).
 *   AC4  — prompt 2 suppressed on sections ≤ 5 min (prompt 1 only); on longer
 *          sections prompt 2 always lands after prompt 1 (one prompt per tick,
 *          prompt 1 evaluated first).
 *   AC5  — never fires for on-pace sections (only once past planned + threshold).
 *   AC6  — base cadence: at most one prompt 1 and one prompt 2 per section (fired
 *          sets keyed by section id); no repeat pinging while on that section.
 *   AC7  — re-entry re-prompt: one extra prompt (escalation copy) ~1 continuous
 *          minute after returning to a section already past planned; bounded to
 *          one per entry and gated by a cooldown so in/out navigation can't spam.
 *   AC8  — copy: prompt 1 = "running long / consider moving on"; prompt 2 and the
 *          re-entry re-prompt = "still running long / look to wrap up".
 *   AC9  — sections with no `duration_minutes` are bad data; skipped, not paced.
 *   AC10 — never fires while paused (caller passes `enabled: false` when paused).
 *   AC11 — exposes `suppressClosingNudge` once a per-section prompt fires on the
 *          FINAL agenda section, so the page can drop MED-1222's SI closing nudge.
 */

/**
 * AC2 — prompt 1 fires once the current section's elapsed exceeds its authored
 * budget by this many seconds (planned + 1 min). Length-independent; the "you're
 * a little behind" signal. Plain export so it's trivially tunable in UAT.
 */
export const PROMPT_1_OVERRUN_SECONDS = 60;

/**
 * AC3 — prompt 2 fires once elapsed reaches `budget × ratio` (~20% over). The
 * "you're now meaningfully over" escalation, proportional to section length.
 */
export const PROMPT_2_OVERRUN_RATIO = 1.2;

/**
 * AC4 — sections whose authored `duration_minutes` is at or below this get
 * prompt 1 only. At ≤ 5 min the proportional 1.2× threshold arrives at or before
 * the fixed +1 min one, so prompt 2 would fire out of order / back-to-back.
 */
export const SHORT_SECTION_MAX_MINUTES = 5;

/**
 * AC7 — how long the coach must be continuously back on a re-entered section
 * (that was already past planned) before the single re-prompt fires.
 */
export const RE_ENTRY_MIN_SECONDS = 60;

/**
 * AC7 — a re-prompt is suppressed if any pacing prompt already fired for that
 * section within this window, so hopping in and out repeatedly can't spam. Open
 * question in the ticket left the exact value to dev; a few minutes is sensible.
 */
export const RE_ENTRY_COOLDOWN_SECONDS = 180;

/**
 * Id prefix for locally-computed pacing nudges. Lets the page distinguish our
 * per-section nudges (keep) from MED-1222's SI closing nudge (also
 * `subtype: 'pacing'`, SI-generated id) when de-duping for AC11.
 */
export const AGENDA_PACE_ID_PREFIX = 'agenda-pace:';

/** AC8 — prompt 1 copy (early nudge). */
const prompt1Message = (label: string): string =>
  `You're running long on '${label}'. Consider moving on to stay on schedule.`;

/** AC8 — prompt 2 + re-entry re-prompt copy (escalation of prompt 1, not a repeat). */
const prompt2Message = (label: string): string =>
  `You're still running long on '${label}'. Look to wrap up to stay on schedule.`;

export interface UseAgendaPacingOptions {
  /** The live active-session state (sections, current index, per-section time). */
  state: ActiveSessionState | null;
  /** ms epoch from the page's shared 1s ticker; drives the per-tick check. */
  nowMs: number;
  /**
   * AC1/AC10 — run only while the in-session AI surface is active (which already
   * requires the `live_session_delivery` flag) AND the session is in progress
   * AND not paused. The caller composes those; the hook simply idles when false.
   */
  enabled: boolean;
  /** Emit a locally-computed pacing nudge into the shared nudge stream. */
  onPacingNudge: (nudge: SessionNudge) => void;
}

export interface UseAgendaPacingResult {
  /**
   * AC11 — true once a per-section pacing prompt has fired for the FINAL agenda
   * section (the session's closing stretch). The page uses it to suppress
   * MED-1222's SI closing nudge so the two pacing signals don't stack.
   */
  suppressClosingNudge: boolean;
}

export function useAgendaPacing({
  state,
  nowMs,
  enabled,
  onPacingNudge,
}: UseAgendaPacingOptions): UseAgendaPacingResult {
  // AC6 — base overrun prompts are one-per-section (keyed by section id), so
  // they don't reset on re-entry; only the re-entry re-prompt is per-visit.
  const firedPrompt1 = useRef<Set<string>>(new Set());
  const firedPrompt2 = useRef<Set<string>>(new Set());
  // AC7 — per-ENTRY guard: entry keys (`${sectionId}@${enteredAt}`) that have
  // already re-prompted, so at most one re-prompt fires per return.
  const repromptedEntries = useRef<Set<string>>(new Set());
  // AC7 — ms epoch of the last pacing prompt per section, for the re-prompt cooldown.
  const lastPromptMsBySection = useRef<Map<string, number>>(new Map());
  const [suppressClosingNudge, setSuppressClosingNudge] = useState(false);

  // Cadence + suppression are per-session facts — reset only when the session
  // itself changes, not on every re-render or pause toggle.
  const sessionId = state?.session_id ?? null;
  useEffect(() => {
    firedPrompt1.current = new Set();
    firedPrompt2.current = new Set();
    repromptedEntries.current = new Set();
    lastPromptMsBySection.current = new Map();
    setSuppressClosingNudge(false);
  }, [sessionId]);

  useEffect(() => {
    if (!enabled || !state) return;

    const idx = state.current_section_index;
    if (idx === null) return;

    const section = state.sections[idx];
    if (!section) return;

    // AC9 — an untimed section is a bad-data outlier (duration is required for
    // authored content to be complete). Skip it; pace the sections that have one.
    const duration = section.duration_minutes;
    if (duration === null || duration <= 0) return;

    const budgetSec = duration * 60;
    // Total time on this section (accumulated prior visits + live this entry).
    const elapsedSec = computeSectionElapsedSeconds(state, idx, nowMs);
    // Time accumulated on PRIOR visits only (server total, before this entry).
    // `> budget` here means the coach RE-ENTERED a section already past planned.
    const priorSec = state.section_time_seconds[String(idx)] ?? 0;
    const enteredAt = state.current_section_entered_at;
    // Continuous seconds on THIS entry — drives the ~1-minute re-prompt delay.
    const liveThisEntrySec = enteredAt
      ? Math.max(0, Math.floor((nowMs - new Date(enteredAt).getTime()) / 1000))
      : 0;
    const entryKey = `${section.id}@${enteredAt ?? 'na'}`;
    const label = section.section_name?.trim() || 'this section';
    const isLastSection = idx === state.sections.length - 1;

    const emit = (idSuffix: string, message: string): void => {
      onPacingNudge({
        id: `${AGENDA_PACE_ID_PREFIX}${section.id}:${idSuffix}`,
        type: 'nudge',
        subtype: 'pacing',
        message,
        timestamp: new Date(nowMs).toISOString(),
        dismissed: false,
        pinned: false,
      });
      lastPromptMsBySection.current.set(section.id, nowMs);
      // AC11 — a prompt on the LAST authored section means we're in the closing
      // stretch, where MED-1222's SI closing nudge also fires. Flag it so the
      // page suppresses the closing nudge and the two don't stack.
      if (isLastSection) setSuppressClosingNudge(true);
    };

    // At most one prompt per tick, evaluated prompt 1 → prompt 2 → re-prompt.
    // Prompt 1 first guarantees AC4's "prompt 2 always lands after prompt 1".

    // AC2 — prompt 1: planned + 1 min, length-independent, names the section.
    if (
      elapsedSec > budgetSec + PROMPT_1_OVERRUN_SECONDS &&
      !firedPrompt1.current.has(section.id)
    ) {
      firedPrompt1.current.add(section.id);
      emit('p1', prompt1Message(label));
      return;
    }

    // AC3/AC4 — prompt 2: planned × 1.2, ONLY on sections longer than 5 min.
    if (
      duration > SHORT_SECTION_MAX_MINUTES &&
      elapsedSec >= budgetSec * PROMPT_2_OVERRUN_RATIO &&
      !firedPrompt2.current.has(section.id)
    ) {
      firedPrompt2.current.add(section.id);
      emit('p2', prompt2Message(label));
      return;
    }

    // AC7 — re-entry re-prompt: the coach returned to a section that was already
    // past planned (prior-visit time alone exceeds budget) and has now been back
    // on it for ~1 continuous minute. One per entry, and only if no pacing prompt
    // fired for the section within the cooldown (so in/out navigation can't spam).
    if (
      priorSec > budgetSec &&
      liveThisEntrySec >= RE_ENTRY_MIN_SECONDS &&
      !repromptedEntries.current.has(entryKey)
    ) {
      const lastPromptMs = lastPromptMsBySection.current.get(section.id);
      const cooldownPassed =
        lastPromptMs === undefined ||
        nowMs - lastPromptMs >= RE_ENTRY_COOLDOWN_SECONDS * 1000;
      if (cooldownPassed) {
        repromptedEntries.current.add(entryKey);
        emit(`re:${enteredAt ?? 'na'}`, prompt2Message(label));
      }
    }
  }, [enabled, state, nowMs, onPacingNudge]);

  return { suppressClosingNudge };
}
